// ─── Cluster E — Banners & Messages (~250 LoC) ────────────────────────────────
// Per docs/design/app-js-split-plan-260522.md §3 Module 4.
//
// Exports: showSectionBanner, hideSectionBanner, showBothSectionBanners,
//          showRateLimitNotification, showUploadErrors, addUploadMessage,
//          updateMessagesSummary, clearUploadMessages, updateSystemBanner,
//          refreshSystemBanner, formatWait.
//
// Globals (read-only at call time):
//   window.adatool.esc       — HTML-escape helper (loaded before this module via esc.js)
//   document                 — DOM access
//   window._adaMaxFileSizeMb — bridged from auth-and-billing.mjs (Phase 9 replaces)
//
// WHY no import of esc from globals-boot.mjs: globals-boot.mjs has DOM side
// effects at module-eval time (document.body.setAttribute). Reading window.adatool.esc
// lazily at call time avoids pulling that side-effect chain into import-time.

/** @param {string} s @returns {string} */
function _esc(s) {
    return (typeof window !== 'undefined' && typeof window.adatool?.esc === 'function')
        ? window.adatool.esc(s)
        : String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

// ─── Section banners (per-section transient, remediate / check) ───────────────

/** @param {string} id @returns {HTMLElement | null} */
function _el(id) {
    return (typeof document !== 'undefined') ? document.getElementById(id) : null;
}

function _remediateBanner() { return _el('remediate-banner'); }

/**
 * @param {string} _section
 * @param {string} type
 * @param {string} msg
 */
export function showSectionBanner(_section, type, msg) {
    const el = _remediateBanner();
    if (!el) return;
    el.className = `section-banner ${type}`;
    el.textContent = msg;
    if (type !== 'error') {
        setTimeout(() => { el.className = 'section-banner hidden'; }, 5000);
    }
}

/** @param {string} _section */
export function hideSectionBanner(_section) {
    const el = _remediateBanner();
    if (el) el.className = 'section-banner hidden';
}

/**
 * @param {string} type
 * @param {string} msg
 */
export function showBothSectionBanners(type, msg) {
    showSectionBanner('remediate', type, msg);
}

// ─── Upload message pane (batched, collapsible, persistent) ───────────────────

/**
 * @param {{ size: string[], pages: string[], tokenGate: string[], format: string[], server: string[], quota?: boolean, anonUnavailable?: boolean, auth?: string[], bootstrapOrigin?: string[] }} errors
 */
export function showUploadErrors(errors) {
    const pane = _el('upload-messages');
    const list = _el('upload-messages-list');
    if (!pane || !list) return;

    const maxFileSizeMb = (typeof window !== 'undefined' && typeof window._adaMaxFileSizeMb === 'number')
        ? window._adaMaxFileSizeMb
        : 25;

    if (errors.size.length > 0) {
        addUploadMessage(list,
            `${errors.size.length} file(s) skipped — exceeds ${maxFileSizeMb} MB limit`,
            errors.size);
    }
    if (errors.pages.length > 0) {
        addUploadMessage(list,
            `${errors.pages.length} file(s) skipped — exceeds page/slide limit for your plan`,
            errors.pages);
    }
    if (errors.tokenGate.length > 0) {
        addUploadMessage(list,
            `${errors.tokenGate.length} file(s) skipped — quota exhausted or rate limit (please retry or upgrade)`,
            errors.tokenGate);
    }
    if (errors.format.length > 0) {
        addUploadMessage(list,
            `${errors.format.length} file(s) skipped — unsupported file type`,
            errors.format);
    }
    if (errors.server.length > 0) {
        addUploadMessage(list,
            `${errors.server.length} file(s) failed — server error`,
            errors.server);
    }
    if (errors.auth && errors.auth.length > 0) {
        addUploadMessage(list,
            `${errors.auth.length} file(s) failed — could not establish an upload session. Please reload the page and try again.`,
            errors.auth);
    }
    // Bootstrap ORIGIN rejection (403): the URL the user is on is not
    // allowlisted server-side (typically a non-canonical Cloud Run URL form).
    // Reloading the SAME URL will 403 again — the actionable fix is to use the
    // canonical app URL. LOUD-failure: never let this be a silent upload no-op.
    // See docs/ops/supabase-redirect-allowlist.md §"anon-bootstrap origin".
    if (errors.bootstrapOrigin && errors.bootstrapOrigin.length > 0) {
        addUploadMessage(list,
            `${errors.bootstrapOrigin.length} file(s) failed — could not start a session on this URL. `
            + 'This web address is not recognized for uploads. Please use the canonical app URL '
            + '(check the link you were given, or sign in) and try again.',
            errors.bootstrapOrigin);
    }
    if (errors.quota) {
        addUploadMessage(list,
            'Quota exceeded. <a href="/account">Manage your account</a> to upgrade.',
            []);
    }
    // Anon-grant fail-closed (S-P2): a TRANSIENT server-side error, distinct
    // from a real quota exhaustion. Make the condition obvious + actionable —
    // "fail-closed SILENT is worse" (do not bury it in the generic quota or
    // server-error buckets). The drop target stays enabled so the user can
    // retry once the service recovers.
    if (errors.anonUnavailable) {
        addUploadMessage(list,
            'Upload could not start — the anonymous upload service is temporarily '
            + 'unavailable. Please try again in a moment, or sign in to continue.',
            []);
    }

    if (list.children.length > 0) {
        pane.style.display = '';
        updateMessagesSummary();
    }
}

/**
 * @param {HTMLElement} container
 * @param {string} summary
 * @param {string[]} filenames
 */
export function addUploadMessage(container, summary, filenames) {
    if (typeof document === 'undefined') return;
    const msg = document.createElement('div');
    msg.className = 'upload-message';
    msg.style.cssText = 'background:var(--error-bg);border:1px solid #fca5a5;border-radius:var(--radius);padding:8px 12px;margin-bottom:6px;font-size:13px';

    let detailsHtml = '';
    if (filenames.length > 0) {
        const items = filenames.map(n => `<li>${_esc(n)}</li>`).join('');
        detailsHtml = `<div class="msg-details hidden" style="margin-top:6px;font-size:12px;color:var(--text-muted)"><ul style="margin:0;padding-left:16px">${items}</ul></div>`;
    }

    const toggleBtn = filenames.length > 0
        ? `<button class="msg-toggle" data-action="toggle-details" style="background:none;border:none;color:var(--accent);cursor:pointer;font-size:12px">&#9656; Details</button>`
        : '';

    msg.innerHTML = `<div style="display:flex;justify-content:space-between;align-items:center"><span>&#9888; ${_esc(summary)}</span><span>${toggleBtn}<button data-action="dismiss-message" style="background:none;border:none;color:var(--text-muted);cursor:pointer;font-size:12px">&times;</button></span></div>${detailsHtml}`;
    container.appendChild(msg);
}

export function updateMessagesSummary() {
    const list = _el('upload-messages-list');
    const pane = _el('upload-messages');
    const summary = _el('upload-messages-summary');
    const count = list ? list.children.length : 0;
    if (count === 0) {
        if (pane) pane.style.display = 'none';
    } else {
        if (summary) summary.textContent = `${count} issue(s)`;
    }
}

export function clearUploadMessages() {
    const list = _el('upload-messages-list');
    if (list) list.innerHTML = '';
    const pane = _el('upload-messages');
    if (pane) pane.style.display = 'none';
}

// ─── Rate limit notification (persistent, dismissable) ────────────────────────

/** @param {string} serverMsg */
export function showRateLimitNotification(serverMsg) {
    const el = _el('rate-limit-notification');
    const msgSpan = _el('rate-limit-msg');
    if (!el || !msgSpan) return;
    msgSpan.textContent = serverMsg || 'You have reached a rate limit.';
    el.removeAttribute('hidden');
}

// ─── System / queue banner ─────────────────────────────────────────────────────

/** @type {unknown} */
let lastQueueState = null;

export function refreshSystemBanner() {
    if (lastQueueState) updateSystemBanner(lastQueueState);
}

/** @param {unknown} q */
export function updateSystemBanner(q) {
    lastQueueState = q;
    // No backpressure — system always accepts uploads.
    // Banner functions kept for future use but nothing to show.
}

// ─── Wait-time formatter ───────────────────────────────────────────────────────

/** @param {number} seconds @returns {string} */
export function formatWait(seconds) {
    if (seconds < 60) return `${seconds}s`;
    const min = Math.ceil(seconds / 60);
    return min === 1 ? '~1 minute' : `~${min} minutes`;
}
